import { useMemo, useState } from 'react';
import axios from 'axios';
import './ConfirmSite.css';

function countPastedReviews(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return 0;
  return trimmed.split(/\r?\n\s*\r?\n+/).map((review) => review.trim()).filter(Boolean).length;
}

export default function PasteReviews({ siteId, disabled = false, onImported }) {
  const [reviewsText, setReviewsText] = useState('');
  const [source, setSource] = useState('Manual import');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const reviewCount = useMemo(() => countPastedReviews(reviewsText), [reviewsText]);

  const importReviews = async () => {
    if (!siteId || !reviewCount || saving || disabled) return;
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/sites/${siteId}/import-reviews`,
        { reviewsText, source },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const added = Number(response.data?.addedCount || 0);
      const duplicates = Number(response.data?.duplicateCount || 0);
      const warnings = Array.isArray(response.data?.warnings) ? response.data.warnings : [];
      const parts = [
        added === 1 ? '1 review added.' : `${added} reviews added.`,
        duplicates ? `${duplicates} duplicate${duplicates === 1 ? '' : 's'} skipped.` : '',
        ...warnings,
      ].filter(Boolean);
      setMessage(parts.join(' '));
      setReviewsText('');
      if (onImported) onImported(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to import reviews.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-slate-950/50 p-4">
      <div className="text-sm font-semibold text-white">Paste Reviews</div>
      <p className="mt-1 text-sm leading-6 text-slate-400">
        Paste reviews from any source. Separate each review with a blank line.
      </p>
      <label className="mt-4 block">
        <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
          Source (optional)
        </span>
        <input
          className="confirm-site-input mt-2"
          value={source}
          onChange={(event) => setSource(event.target.value)}
          placeholder="e.g. Facebook, TripAdvisor or customer survey"
          maxLength={100}
          disabled={disabled || saving}
        />
      </label>
      <label className="mt-4 block">
        <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
          Reviews
        </span>
        <textarea
          className="confirm-site-textarea mt-2"
          rows={7}
          value={reviewsText}
          onChange={(event) => setReviewsText(event.target.value)}
          placeholder={'Fantastic food and friendly staff.\n\nGreat atmosphere and live music.'}
          disabled={disabled || saving}
        />
      </label>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-slate-400">
          {reviewCount} review{reviewCount === 1 ? '' : 's'} detected
        </span>
        <button
          type="button"
          className="confirm-site-button confirm-site-button-secondary"
          onClick={importReviews}
          disabled={disabled || saving || !siteId || reviewCount === 0}
        >
          {saving ? 'Saving and analysing…' : 'Add and analyse reviews'}
        </button>
      </div>
      {message ? <p className="mt-3 text-sm text-emerald-300">{message}</p> : null}
      {error ? <p className="mt-3 text-sm text-red-300">{error}</p> : null}
    </div>
  );
}

export { countPastedReviews };
